import { Fragment, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  NvAlertDialog,
  NvButton,
  NvConsentBox,
  NvConsentPromptDialog,
  NvFormField,
  NvInfoIcon,
  NvRequiredLabel,
  NvTermsModal,
  NvTextarea,
  NV_PRIMARY_DISABLED_CLASS,
  type NvConsentItem,
} from "@/components/navigator";
import { useAuth } from "@/contexts/auth-context";
import { isMemberLevel } from "@/lib/access";
import { getApiError, getApiErrorMessage, getApiFieldErrors } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import {
  agreeThirdParty,
  getFormContext,
  submitGuestInquiry,
  submitMemberInquiry,
  type ContactTarget,
  type FormContext,
  type InquirySource,
} from "@/services/navigator/contact-service";

// F05 Contact Us (Figma ver.7): 회원 (수요자 847:45040 · 공급자 847:54484 · PwC 내부 847:54542) · 비회원 847:45109 · 승인 대기.
// 접수 후 /contact/complete (847:45159). 타이틀은 모든 화면 "Contact Us" (PwC 내부 "문의하기").
// 동의 박스는 회원 · 비회원 모두 "모두 동의합니다" + 이용약관 · 개인정보 처리방침 · 개인정보 수집·이용 동의 (필수) + 제3자 제공.
// 제3자 제공은 선택 동의라 도입 · 컨설팅 문의(공급사 · 컨설턴트에게 연락처가 전달되는 문의)일 때만 (필수)로 보이고,
// 동의하지 않은 채 그 유형을 고르거나 제출하면 "개인정보 제3자 제공 동의 안내" 팝업으로 받아요 (847:57620, 정의서 05 p.6 ⑧)
const THIRD_PARTY_CODE = "supplier_contact_share";
const OPTIONAL_CONSENTS = [THIRD_PARTY_CODE];
const THIRD_PARTY_MESSAGE = "해당 서비스는 개인정보 제3자 제공 동의서에 동의하셔야 사용하실 수 있습니다.";
// 동의 항목 (회원 · 비회원 공통, Figma ver.7). 제3자 제공 줄의 (필수) · (선택)은 선택한 문의 유형에 따라 정해요
const consentItems = (thirdPartyRequired: boolean): NvConsentItem[] => [
  { code: "service_terms", label: "이용약관 (필수)", viewable: true },
  { code: "privacy_policy", label: "개인정보 처리방침 (필수)", viewable: true },
  { code: "privacy_inquiry", label: "개인정보 수집·이용 동의 (필수)", viewable: true },
  { code: THIRD_PARTY_CODE, label: `개인정보 제3자 제공 동의 (${thirdPartyRequired ? "필수" : "선택"})`, viewable: true },
];
const TERMS_CODES = consentItems(false).map((item) => item.code);
// 약관 전문 보기 탭 이름은 회원가입과 같은 팝업이라 같은 이름 (Figma ver.7 847:47470)
const TERMS_TAB_LABELS = { privacy_inquiry: "개인정보 수집 및 이용 동의서", supplier_contact_share: "개인정보 제3자 제공 동의서" };
const SOURCES: InquirySource[] = ["gnb", "agent_detail", "case_study", "ai_assistant"];

// 유형별 안내 문구 (정의서 05)
const GUIDES: Record<string, string> = {
  system: "이용 중 불편한 점이나 오류를 알려주세요. 확인 후 회신드립니다.",
  adoption:
    "문의 내용과 연락처를 해당 공급사에 전달하며, 이후 안내는 공급사가 직접 진행합니다. 여러 에이전트를 비교하거나 여러 부서에 걸친 도입을 검토 중이라면 [컨설팅 문의]를 이용해 보세요.",
  consulting: "담당 컨설턴트의 답변은 My Page와 이메일로 확인할 수 있습니다. 상담이 필요하시면 회신 메일에서 편한 일정을 정하실 수 있습니다.",
  company_info: "플랫폼에 등록된 회사 정보나 담당자 정보 변경을 요청해 주세요. 반영 결과는 My Page와 이메일로 확인할 수 있습니다.",
  registration_review:
    "에이전트와 Case Study의 등록 절차, 검수 진행 상황, 보완 요청 사항에 대해 문의해 주세요. 답변은 My Page와 이메일로 확인할 수 있습니다.",
};
// PwC 내부는 컨설팅 문의가 없어 도입 문의 안내에서 컨설팅 권유 문장을 빼요
const INTERNAL_GUIDES: Record<string, string> = {
  adoption: "문의 내용과 연락처를 해당 공급사에 전달하며, 이후 안내는 공급사가 직접 진행합니다.",
};

type FormKey = "name" | "position" | "email" | "phone" | "company" | "industry" | "company_size" | "content";
// 회원 유형별 입력 항목 (2열 순서대로) — 백엔드 contact_service.REQUIRED_FIELDS_BY_LEVEL과 같아요
const FIELDS_BY_LEVEL: Record<string, FormKey[]> = {
  demand: ["name", "position", "email", "phone", "company", "industry", "company_size"],
  supplier: ["name", "email", "position", "phone"],
  internal: ["name", "position", "email", "phone"],
};

const formatPhone = (raw: string) => {
  const d = raw.replace(/\D/g, "").slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, d.length - 4)}-${d.slice(-4)}`;
};
// 연락처는 숫자 9~11자리 (서버 검증과 같아요). 아니면 입력 칸 아래에 "연락처 형식을 확인해 주세요" (Figma ver.7 입력 오류 상태)
const PHONE_ERROR = "연락처 형식을 확인해 주세요 (예 : 010-1234-5678)";
const validPhone = (phone: string) => /^\d{9,11}$/.test(phone.replace(/\D/g, ""));

export function NavigatorContactPage() {
  const { accessLevel, isLoading } = useAuth();
  const isMember = isMemberLevel(accessLevel);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const target = useMemo<ContactTarget>(() => {
    const source = searchParams.get("source") as InquirySource | null;
    return {
      source: source && SOURCES.includes(source) ? source : "gnb",
      agent_id: searchParams.get("agent_id") ?? undefined,
      case_study_id: searchParams.get("case_study_id") ?? undefined,
      assistant_session_id: searchParams.get("assistant_session_id") ?? undefined,
    };
  }, [searchParams]);
  // Return here after "확인" on the completion screen (IA 210 "직전 화면 복귀")
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const [context, setContext] = useState<FormContext | null>(null);
  const [inquiryType, setInquiryType] = useState("system");
  // AI Assistant 상담에서 넘어오면 정리해 둔 문의 초안(assistantDraft)을 내용 칸에 채워요
  const assistantDraft = (location.state as { assistantDraft?: unknown } | null)?.assistantDraft;
  const [form, setForm] = useState({
    name: "", position: "", email: "", phone: "", company: "", industry: "", company_size: "",
    content: typeof assistantDraft === "string" ? assistantDraft : "",
  });
  const [consents, setConsents] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [termsCode, setTermsCode] = useState<string | null>(null);
  const [popup, setPopup] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  // 제3자 제공 동의 팝업: "select" = 도입 · 컨설팅 선택(진입) 때, "submit" = 동의 없이 [제출하기] 때 (동의하면 바로 접수)
  const [thirdPartyPrompt, setThirdPartyPrompt] = useState<"select" | "submit" | null>(null);
  const [agreeing, setAgreeing] = useState(false);

  // 박스의 제3자 제공 체크가 이 문의의 동의예요. 이미 동의한 회원은 처음에 체크된 상태로 보여 주고, 언제든 풀 수 있어요
  const needsThirdParty = (type: string) => !consents[THIRD_PARTY_CODE] && !!context?.third_party_types.includes(type);

  useEffect(() => {
    if (isLoading || !isMember) return;
    getFormContext(target)
      .then((ctx) => {
        setContext(ctx);
        setInquiryType(ctx.default_type);
        setConsents((c) => ({ ...c, [THIRD_PARTY_CODE]: ctx.third_party_agreed }));
        // '도입 문의하기'로 들어와 도입 문의가 기본 선택이면 바로 안내해요
        if (!ctx.third_party_agreed && ctx.third_party_types.includes(ctx.default_type)) setThirdPartyPrompt("select");
        setForm((f) => ({
          ...f,
          name: ctx.prefill.name ?? "",
          position: ctx.prefill.position ?? "",
          email: ctx.prefill.email ?? "",
          phone: ctx.prefill.phone ? formatPhone(ctx.prefill.phone) : "",
          company: ctx.prefill.company ?? "",
          industry: ctx.prefill.industry ?? "",
          company_size: ctx.prefill.company_size ?? "",
        }));
      })
      .catch((err) => setPopup(getApiErrorMessage(err)));
  }, [isLoading, isMember, target]);

  const set = (key: keyof typeof form) => (value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: "" } : e));
  };

  // 제3자 제공은 도입 · 컨설팅 문의에서만 (필수)로 보여요 (서버도 그 유형에서만 동의를 요구해요)
  const thirdPartyRequired = isMember && !!context?.third_party_types.includes(inquiryType);
  const items = consentItems(thirdPartyRequired);
  const required = items.map((item) => item.code).filter((code) => !OPTIONAL_CONSENTS.includes(code));
  const consentsOk = required.every((code) => consents[code]);
  const memberFields = FIELDS_BY_LEVEL[accessLevel] ?? FIELDS_BY_LEVEL.demand;
  const requiredFields: FormKey[] = isMember ? [...memberFields, "content"] : ["name", "email", "phone", "company", "content"];
  const fieldsOk = requiredFields.every((key) => form[key].trim());
  const guide = (accessLevel === "internal" && INTERNAL_GUIDES[inquiryType]) || GUIDES[inquiryType];

  const checkPhone = () => {
    if (form.phone.trim() && !validPhone(form.phone)) setErrors((e) => ({ ...e, phone: PHONE_ERROR }));
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!fieldsOk || !consentsOk || submitting) return;
    if (!validPhone(form.phone)) {
      setErrors({ phone: PHONE_ERROR });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setErrors({ email: "이메일 형식을 확인해 주세요." });
      return;
    }
    // 동의 없이 [제출하기] → 같은 팝업, [닫기]면 접수하지 않아요
    if (isMember && needsThirdParty(inquiryType)) {
      setThirdPartyPrompt("submit");
      return;
    }
    void send();
  };

  const selectType = (type: string) => {
    setInquiryType(type);
    if (needsThirdParty(type)) setThirdPartyPrompt("select");
  };

  const agreeThirdPartyAndContinue = async () => {
    const mode = thirdPartyPrompt;
    setAgreeing(true);
    try {
      await agreeThirdParty();
      const next = { ...consents, [THIRD_PARTY_CODE]: true };
      setConsents(next);
      setThirdPartyPrompt(null);
      if (mode === "submit") void send(next);
    } catch (err) {
      setThirdPartyPrompt(null);
      setPopup(getApiErrorMessage(err));
    } finally {
      setAgreeing(false);
    }
  };

  // checked: 팝업 [동의하기] 직후처럼 화면 상태가 아직 반영되기 전의 동의 값
  const send = async (checked: Record<string, boolean> = consents) => {
    setSubmitting(true);
    // 박스의 제3자 제공 체크도 함께 보내면 서버가 문의 · 회원 동의로 남겨요 (체크 해제면 철회)
    const agreed = Object.keys(checked).filter((code) => checked[code]);
    try {
      const receipt = isMember
        ? await submitMemberInquiry({
            ...target,
            inquiry_type: inquiryType,
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.replace(/\D/g, ""),
            // 화면에 없는 항목은 보내지 않아요 (공급자 · PwC 내부: 회사 · 업종 · 회사 규모 없음)
            ...Object.fromEntries(
              (["position", "company", "industry", "company_size"] as const)
                .filter((key) => memberFields.includes(key))
                .map((key) => [key, form[key].trim()]),
            ),
            content: form.content.trim(),
            consents: agreed,
            idempotency_key: idempotencyKey,
          })
        : await submitGuestInquiry({
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.replace(/\D/g, ""),
            company: form.company.trim(),
            content: form.content.trim(),
            consents: agreed,
            idempotency_key: idempotencyKey,
          });
      navigate("/contact/complete", { replace: true, state: { receipt, isMember, from } });
    } catch (err) {
      setSubmitting(false);
      // 서버가 동의를 요구하면 같은 팝업으로 받아요
      if (getApiError(err)?.code === "THIRD_PARTY_CONSENT_REQUIRED") {
        setConsents((c) => ({ ...c, [THIRD_PARTY_CODE]: false }));
        setThirdPartyPrompt("submit");
        return;
      }
      setErrors(getApiFieldErrors(err));
      setPopup(getApiError(err)?.message ?? "문의를 접수하지 못했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  if (isLoading) return null;
  const attachment = context?.attachment;
  const req = (text: string) => <NvRequiredLabel>{text}</NvRequiredLabel>;
  // 연락처: 형식이 틀리면 입력 칸 테두리 1.5px 빨강(#E5484D) + 아래 13/400 안내 (Figma ver.7 입력 오류 상태). 서버가 돌려준 오류도 같은 자리에 보여요
  const phoneField = (placeholder: string) => (
    <div className="flex flex-col gap-[6px]">
      <NvFormField
        label={req("연락처")}
        aria-required
        aria-invalid={errors.phone ? true : undefined}
        placeholder={placeholder}
        inputMode="tel"
        value={form.phone}
        onChange={(e) => set("phone")(formatPhone(e.target.value))}
        onBlur={checkPhone}
        inputClassName={errors.phone ? "border-[1.5px] border-[#E5484D]" : undefined}
      />
      {errors.phone && <p className="text-[13px] leading-4 text-[#E5484D]">{errors.phone}</p>}
    </div>
  );

  return (
    <form noValidate onSubmit={onSubmit} className="mx-auto flex w-full max-w-[832px] flex-col gap-6 px-4 pb-20 pt-12">
      <h1 className="text-[28px] font-normal leading-[34px] text-nv-text-primary">{accessLevel === "internal" ? "문의하기" : "Contact Us"}</h1>

      {isMember ? (
        <>
          <p className="text-[13px] font-bold leading-4 text-nv-text-primary">
            <NvRequiredLabel>문의 유형</NvRequiredLabel>
          </p>
          <div role="radiogroup" aria-label="문의 유형" className="grid gap-[10px]" style={{ gridTemplateColumns: `repeat(${context?.inquiry_types.length ?? 3}, minmax(0, 1fr))` }}>
            {context?.inquiry_types.map((type) => (
              <button
                key={type.value}
                type="button"
                role="radio"
                aria-checked={inquiryType === type.value}
                onClick={() => selectType(type.value)}
                className={cn(
                  "rounded-nv-control border py-[14px] text-[14px] leading-[17px] text-nv-text-primary",
                  inquiryType === type.value
                    ? "border-nv-brand bg-nv-brand-subtle font-medium"
                    : "border-nv-border bg-nv-surface font-normal hover:border-nv-border-strong",
                )}
              >
                {type.label}
              </button>
            ))}
          </div>
          {guide && (
            <p className="flex gap-2 rounded-nv-control bg-nv-bg-page px-4 py-3 text-[13px] leading-[20px] text-nv-text-secondary">
              <NvInfoIcon size={16} className="mt-[2px] shrink-0 text-nv-text-primary" />
              {guide}
            </p>
          )}
          {attachment && (
            <div className="flex items-center gap-[10px] rounded-nv-card bg-nv-bg-page px-4 py-3">
              <span className="rounded-nv-pill border border-nv-border bg-nv-surface px-[10px] py-1 text-[11px] font-medium leading-[13px] text-nv-text-secondary">
                자동 첨부
              </span>
              <span className="text-[13px] leading-4 text-nv-text-primary">
                {[
                  attachment.agent_name && `대상 에이전트 ${attachment.agent_name}`,
                  attachment.case_title && `Case Study ${attachment.case_title}`,
                  attachment.supplier_name && `공급사 ${attachment.supplier_name}`,
                  `진입 출처 : ${attachment.source_label}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          )}
        </>
      ) : (
        <p className="flex items-center gap-2 rounded-nv-card bg-nv-warning-fill px-4 py-3 text-[13px] font-medium leading-4 text-nv-text-primary">
          <NvInfoIcon size={16} className="shrink-0" />
          비회원은 시스템 사용 문의만 가능합니다. 도입·컨설팅 문의는 로그인 후 이용해 주세요.
        </p>
      )}

      <div className="grid grid-cols-2 items-center gap-x-5 gap-y-4">
        {isMember ? (
          memberFields.map((key) => {
            const isDemand = accessLevel === "demand";
            switch (key) {
              case "name":
                return <NvFormField key={key} label={req("이름")} aria-required placeholder={isDemand ? "김OO" : "이름을 입력하세요"} value={form.name} onChange={(e) => set("name")(e.target.value)} error={errors.name} maxLength={100} />;
              case "position": {
                // 공급자 화면은 "직함", 수요자 · PwC 내부는 "직급"
                const label = accessLevel === "supplier" ? "직함" : "직급";
                return <NvFormField key={key} label={req(label)} aria-required placeholder={isDemand ? "팀장" : `${label}을 입력하세요`} value={form.position} onChange={(e) => set("position")(e.target.value)} error={errors.position} maxLength={100} />;
              }
              case "email":
                // 회원 이메일은 계정 값으로 고정 (F05 노트)
                return <NvFormField key={key} label={req("이메일")} aria-required type="email" value={form.email} readOnly disabled error={errors.email} />;
              case "phone":
                return <Fragment key={key}>{phoneField(isDemand ? "010-1234-5678" : "010-0000-0000")}</Fragment>;
              case "company":
                return <NvFormField key={key} label={req("회사")} aria-required placeholder="A중공업" value={form.company} onChange={(e) => set("company")(e.target.value)} error={errors.company} maxLength={200} />;
              case "industry":
                return <NvFormField key={key} label={req("업종")} aria-required placeholder="제조업" value={form.industry} onChange={(e) => set("industry")(e.target.value)} error={errors.industry} maxLength={100} />;
              case "company_size":
                return <NvFormField key={key} label={req("회사 규모")} aria-required placeholder="중견기업" value={form.company_size} onChange={(e) => set("company_size")(e.target.value)} error={errors.company_size} maxLength={50} />;
              default:
                return null;
            }
          })
        ) : (
          <>
            <NvFormField label={req("이름")} aria-required placeholder="이름을 입력하세요" value={form.name} onChange={(e) => set("name")(e.target.value)} error={errors.name} maxLength={100} />
            <NvFormField label={req("이메일")} aria-required type="email" placeholder="이메일을 입력하세요" value={form.email} onChange={(e) => set("email")(e.target.value)} error={errors.email} />
            {phoneField("010-0000-0000")}
            <NvFormField label={req("회사")} aria-required placeholder="회사명을 입력하세요" value={form.company} onChange={(e) => set("company")(e.target.value)} error={errors.company} maxLength={200} />
          </>
        )}
      </div>

      <label htmlFor="contact-content" className="text-[13px] font-bold leading-4 text-nv-text-primary">
        <NvRequiredLabel>문의 내용</NvRequiredLabel>
      </label>
      <NvTextarea
        id="contact-content"
        placeholder="문의 내용을 입력하세요"
        value={form.content}
        maxLength={5000}
        onChange={(e) => set("content")(e.target.value)}
        error={errors.content}
      />

      <NvConsentBox items={items} checked={consents} onChange={setConsents} onView={setTermsCode} />

      <div className="flex justify-center">
        <NvButton type="submit" className={NV_PRIMARY_DISABLED_CLASS} disabled={!fieldsOk || !consentsOk || submitting}>
          제출하기
        </NvButton>
      </div>

      <NvConsentPromptDialog
        open={thirdPartyPrompt !== null}
        onOpenChange={(open) => !open && setThirdPartyPrompt(null)}
        title="개인정보 제3자 제공 동의 안내"
        message={THIRD_PARTY_MESSAGE}
        itemLabel="[선택] 개인정보 제3자 제공 동의서"
        onView={() => setTermsCode(THIRD_PARTY_CODE)}
        onAgree={agreeThirdPartyAndContinue}
        agreeing={agreeing}
      />
      <NvTermsModal codes={TERMS_CODES} openCode={termsCode} onOpenCodeChange={setTermsCode} labels={TERMS_TAB_LABELS} />
      <NvAlertDialog
        open={popup !== null}
        onOpenChange={(open) => !open && setPopup(null)}
        message={popup}
        actions={<NvButton onClick={() => setPopup(null)}>확인</NvButton>}
      />
    </form>
  );
}
